import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import './AuthPages.css';

const validateUsername = (username) => {
  if (/^guest_/i.test(username)) return "Username cannot start with 'guest_'";
  if (username.length < 3) return 'Username must be at least 3 characters';
  if (username.length > 20) return 'Username cannot exceed 20 characters';
  if (!/^[a-zA-Z0-9_]+$/.test(username)) return 'Username can only contain letters, numbers, and underscores';
  return null;
};

const RegisterPage = () => {
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const { register, playAsGuest } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!username || !email || !password || !confirmPassword) {
      setError('Please fill in all required fields');
      return;
    }
    const usernameError = validateUsername(username);
    if (usernameError) {
      setError(usernameError);
      return;
    }
    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    try {
      setError('');
      setLoading(true);
      const result = await register(username, email, password);
      if (result.success) {
        setSuccess('Registration successful. Please check your email to verify your account.');
      } else {
        setError(result.message || 'Registration failed. Username or email is already taken.');
      }
    } catch (err) {
      if (err.message && err.message.includes('shorter than the minimum allowed length')) {
        setError('Username must be at least 3 characters long');
      } else {
        setError(err.message || 'Registration failed');
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePlayAsGuest = () => {
    playAsGuest();
    navigate('/');
  };

  return (
    <div className="auth">
      <section className="cr-card auth-card">
        <h1>Register</h1>
        {error && <p className="auth-msg error" role="alert">{error}</p>}
        {success && <p className="auth-msg success" role="status">{success}</p>}

        {!success ? (
          <>
            <form onSubmit={handleSubmit} className="auth-form" noValidate>
              <div className="auth-field">
                <label htmlFor="username">Username</label>
                <input
                  type="text"
                  id="username"
                  className="cr-input"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="At least 3 characters"
                  autoComplete="username"
                  autoCapitalize="none"
                  spellCheck={false}
                />
              </div>
              <div className="auth-field">
                <label htmlFor="email">Email</label>
                <input
                  type="email"
                  id="email"
                  className="cr-input"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Required for account verification"
                  autoComplete="email"
                />
              </div>
              <div className="auth-field">
                <label htmlFor="password">Password</label>
                <input
                  type="password"
                  id="password"
                  className="cr-input"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="At least 6 characters"
                  autoComplete="new-password"
                />
              </div>
              <div className="auth-field">
                <label htmlFor="confirmPassword">Confirm Password</label>
                <input
                  type="password"
                  id="confirmPassword"
                  className="cr-input"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  autoComplete="new-password"
                />
              </div>
              <button type="submit" className="cr-btn solid auth-btn" disabled={loading}>
                {loading ? 'Registering' : 'Register'}
              </button>
            </form>

            <div className="auth-or">or</div>

            <button type="button" className="cr-btn auth-btn" onClick={handlePlayAsGuest}>
              Play as Guest
            </button>

            <div className="auth-links">
              <p>Already have an account? <Link to="/login">Login</Link></p>
            </div>
          </>
        ) : (
          <>
            <p className="cr-muted">
              We've sent a verification link to your email address. Please check your inbox and click the link to activate your account.
            </p>
            <div className="auth-stack">
              <Link to="/login" className="cr-btn solid auth-btn">Login</Link>
              <Link to="/" className="cr-btn auth-btn">Back to Home</Link>
            </div>
          </>
        )}
      </section>
    </div>
  );
};

export default RegisterPage;
